/**
 * The sentence a person reads when something did not work.
 *
 * A rejection's message belongs to whoever threw it. When that is Paige - the engine's typed
 * refusal, a validation, an HTTP failure the adapter turned into an Error - it was written for the
 * person and is the best thing to show them. When it is the language itself, it is a programmer's
 * sentence about a variable. Both went into the same banner: on dev, in the ordinary course of
 * reviewing, Paige told a customer "The current job fields could not be applied: Cannot read
 * properties of undefined (reading 'documents')", and a moment later "Cannot read properties of
 * undefined (reading 'documentType')".
 *
 * A TypeError is the polite case. The same line would as readily print the name of a library, a
 * vendor or a model, which this product never says out loud, and no rule about naming can hold while
 * an arbitrary runtime string is interpolated into customer text.
 *
 * So an error the language raised is answered with the caller's own fallback, which was written for
 * this situation, and everything else is shown as it was written.
 */

/** Errors the runtime raises about the program, never about the person's document. */
const runtimeFaults = [TypeError, ReferenceError, RangeError, SyntaxError, EvalError, URIError];

/** The message to show for a rejection, or `fallback` when it has nothing a person can use. */
export function messageFor(reason: unknown, fallback: string): string {
  if (!(reason instanceof Error)) return fallback;
  if (runtimeFaults.some((fault) => reason instanceof fault)) return fallback;
  const written = reason.message.trim();
  return written.length > 0 ? written : fallback;
}

/** `context`, with the reason after it when the reason was written for a person to read. */
export function messageAfter(context: string, reason: unknown): string {
  const detail = messageFor(reason, '');
  return detail.length > 0 ? `${context}: ${detail}` : context;
}
